import { Button } from '@mui/material';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { JourneyLoadingScreen, StandardJourneyBrandedScreen } from '~/components/StandardJourney';
import { reportError } from '~/utils/reportError';

type AppErrorBoundaryProps = { children: ReactNode };
type AppErrorBoundaryState = { hasError: boolean };

/**
 * Rede de segurança para erros de render.
 *
 * Sem isto, qualquer exceção não apanhada durante o render desmonta a árvore
 * inteira do React e o utilizador fica com uma **página em branco** com a URL
 * correta — exatamente o sintoma que já tínhamos com o gate da credencial.
 * Aqui o erro é reportado ao PostHog e o utilizador vê uma saída.
 */
export default class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError('Erro de render não tratado', error, {
      context: 'AppErrorBoundary',
      componentStack: info.componentStack
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <StandardJourneyBrandedScreen
        appId="app-error-boundary"
        contentLayout="fullscreen"
        progressPercent={0}
        showBack={false}
        showHeaderProgressBar={false}
      >
        <JourneyLoadingScreen
          appId="app-error-boundary-content"
          busy={false}
          message="Algo deu errado por aqui"
          description="Já registramos o problema. Recarregue a página para continuar de onde parou."
          action={
            <Button
              data-app-id="app-error-boundary-reload"
              onClick={this.handleReload}
              size="large"
              variant="contained"
            >
              Recarregar a página
            </Button>
          }
        />
      </StandardJourneyBrandedScreen>
    );
  }
}
